import { ChoiceGroup, TagSelect } from '@skupreme/shared/ui';
import {
  FormCheckbox,
  FormItem,
  FormTextField,
} from '@skupreme/shared/ui/form';
import { StorageProviderHelpText } from '@utility/constants/strings';
import { Controller, type Control } from 'react-hook-form';
import { quantityTypeOptions } from '../../lib/integration';
import type { ProviderDetailsValues } from '../../lib/provider-details';
import { KindLabel } from '../KindLabel';

type FieldProps = {
  control: Control<ProviderDetailsValues>;
  isDisabled: boolean;
};

const QUANTITY_OPTIONS = quantityTypeOptions.map(option => ({
  value: option.value,
  label: option.text,
}));

/** A yes/no provider setting with its own explanation under a field label. */
export function KindField({
  control,
  name,
  label,
  hint,
  title,
  detail,
  isDisabled,
}: FieldProps & {
  name: 'is_fulfillable' | 'is_warehouse' | 'is_oos_fulfillable';
  label: string;
  hint: string;
  title: string;
  detail: string;
}) {
  return (
    <FormItem label={label} hint={hint}>
      <FormCheckbox
        control={control}
        name={name}
        label={<KindLabel title={title} detail={detail} />}
        isDisabled={isDisabled}
      />
    </FormItem>
  );
}

export function QuantityTypeField({ control, isDisabled }: FieldProps) {
  return (
    <Controller
      control={control}
      name="quantity_type"
      render={({ field }) => (
        <FormItem
          label="Quantity Type"
          hint={StorageProviderHelpText.quantityTypeInfoUpdate}
        >
          <ChoiceGroup
            label="Quantity Type"
            options={QUANTITY_OPTIONS}
            value={field.value}
            onChange={field.onChange}
            isDisabled={isDisabled}
          />
        </FormItem>
      )}
    />
  );
}

/** The name is printed only while the box is ticked; unticked, it is not sent. */
export function SenderNameField({
  control,
  isEnabled,
  isDisabled,
}: FieldProps & { isEnabled: boolean }) {
  return (
    <div className="sk:flex sk:flex-col sk:gap-1">
      <FormTextField
        control={control}
        name="label_name"
        label="Sender Name"
        placeholder="Sender Name"
        isDisabled={isDisabled || !isEnabled}
      />
      <FormCheckbox
        control={control}
        name="use_label_name"
        label="Print the sender name on labels"
        hint={StorageProviderHelpText.useLabelName}
        isDisabled={isDisabled}
      />
    </div>
  );
}

export function WarehouseCodesField({ control, isDisabled }: FieldProps) {
  return (
    <Controller
      control={control}
      name="warehouse_codes"
      render={({ field }) => {
        const values = field.value ?? [];
        return (
          <FormItem label="Amazon vendor warehouse codes">
            <TagSelect
              aria-label="Amazon vendor warehouse codes"
              placeholder="Create Amazon vendor warehouse code"
              options={[...values]
                .sort()
                .map(code => ({ value: code, label: code }))}
              values={values}
              onChange={field.onChange}
              onBlur={field.onBlur}
              allowsCustomValue
              isDisabled={isDisabled}
              className="sk:w-full"
            />
          </FormItem>
        );
      }}
    />
  );
}
